import "@/App.css";
import { useEffect } from "react";
import { MotionConfig } from "framer-motion";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { ThemeProvider } from "@/components/styleguide/ThemeProvider";
import FoundationsPage from "@/pages/FoundationsPage";
import LibraryPage from "@/pages/LibraryPage";
import ArchitecturePage from "@/pages/ArchitecturePage";
import AdditionStory from "@/experiences/year1/addition/AdditionStory";
import WaterStory from "@/experiences/year1/water-the-plant/WaterStory";
import HomeWorld from "@/experiences/home/HomeWorld";
import SelectChild from "@/experiences/select-child/SelectChild";
import { AuthProvider, RequireAuth } from "@/vocab/AuthContext";
import AuthCallback from "@/vocab/AuthCallback";
import VocabLanding from "@/vocab/VocabLanding";
import ChildrenPage from "@/vocab/ChildrenPage";
import ParentDashboard from "@/vocab/ParentDashboard";
import ChildHome from "@/vocab/ChildHome";
import LearnPage from "@/vocab/LearnPage";
import QuizPage from "@/vocab/QuizPage";
import PlansPage from "@/vocab/PlansPage";
import PlanCreatePage from "@/vocab/PlanCreatePage";
import ChildPlansPage from "@/vocab/ChildPlansPage";
import RevisionLanding from "@/vocab/RevisionLanding";
import { ParentGate } from "@/vocab/ParentGate";
import ParentWordRequest from "@/vocab/ParentWordRequest";
import SubscriptionGate from "@/vocab/SubscriptionGate";   // ← NEW
import AdminLayout from "@/vocab/admin/AdminLayout";
import AdminDashboard from "@/vocab/admin/AdminDashboard";
import AdminWords from "@/vocab/admin/AdminWords";
import AdminImport from "@/vocab/admin/AdminImport";
import AdminReview from "@/vocab/admin/AdminReview";
import AdminFlags from "@/vocab/admin/AdminFlags";
import AdminMissing from "@/vocab/admin/AdminMissing";
import AdminUsers from "@/vocab/admin/AdminUsers";
import AdminSettings from "@/vocab/admin/AdminSettings";
import PrivacyPage from "@/vocab/PrivacyPage";
import TermsPage from "@/vocab/TermsPage";
import RefundPolicyPage from "@/vocab/RefundPolicyPage";

// ── Billing success / cancel landing page ─────────────────────────────────────
function BillingPage() {
  const [params] = useSearchParams();
  const success  = params.get("success") === "1";
  const cancelled = params.get("cancelled") === "1";
  return (
    <RequireAuth>
      <ParentGate>
        <div className="flex min-h-[100svh] flex-col items-center justify-center gap-3 bg-background px-6 text-center">
          {success ? (
            <>
              <p className="font-display text-3xl font-black text-foreground">You're all set! 🎉</p>
              <p className="max-w-sm text-sm font-semibold text-muted-foreground">
                Your Bloom subscription is now active. Head back to your dashboard to keep learning.
              </p>
              <a href="/vocab/parent" className="mt-3 font-display font-extrabold text-primary-strong underline">
                Go to dashboard →
              </a>
            </>
          ) : cancelled ? (
            <>
              <p className="font-display text-2xl font-black text-foreground">No problem — you can choose a plan any time.</p>
              <a href="/vocab/parent" className="mt-3 font-display font-extrabold text-primary-strong underline">
                Back to dashboard
              </a>
            </>
          ) : (
            <a href="/vocab/parent" className="font-display font-extrabold text-primary-strong underline">
              Back to dashboard
            </a>
          )}
        </div>
      </ParentGate>
    </RequireAuth>
  );
}

// ── Gated parent route wrapper ────────────────────────────────────────────────
function ParentRoute({ children }) {
  return (
    <RequireAuth>
      <ParentGate>
        <SubscriptionGate>
          {children}
        </SubscriptionGate>
      </ParentGate>
    </RequireAuth>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<FoundationsPage />} />
      <Route path="/library" element={<LibraryPage />} />
      <Route path="/architecture" element={<ArchitecturePage />} />
      <Route path="/select-child" element={<SelectChild />} />
      <Route path="/home/:yearId" element={<HomeWorld />} />
      <Route path="/experiences/year1/addition" element={<AdditionStory />} />
      <Route path="/experiences/year1/water-the-plant" element={<WaterStory />} />

      {/* Bloom Vocabulary */}
      <Route path="/vocab" element={<VocabLanding />} />
      <Route path="/vocab/billing" element={<BillingPage />} />

      {/* Public — no auth required */}
      <Route path="/vocab/privacy"        element={<PrivacyPage />} />
      <Route path="/vocab/terms"          element={<TermsPage />} />
      <Route path="/vocab/refund-policy"  element={<RefundPolicyPage />} />

      <Route path="/vocab/children" element={<ParentRoute><ChildrenPage /></ParentRoute>} />
      <Route path="/vocab/parent"   element={<ParentRoute><ParentDashboard /></ParentRoute>} />
      <Route path="/vocab/parent/word-requests" element={<ParentRoute><ParentWordRequest /></ParentRoute>} />

      {/* Parent — Plans */}
      <Route path="/vocab/parent/child/:childId/plans"              element={<ParentRoute><PlansPage /></ParentRoute>} />
      <Route path="/vocab/parent/child/:childId/plans/create"        element={<ParentRoute><PlanCreatePage /></ParentRoute>} />
      <Route path="/vocab/parent/child/:childId/plans/:planId/edit" element={<ParentRoute><PlanCreatePage /></ParentRoute>} />

      {/* Child — not subscription-gated (parent auth covers access) */}
      <Route path="/vocab/child/:childId"            element={<RequireAuth><ChildHome /></RequireAuth>} />
      <Route path="/vocab/child/:childId/learn"      element={<RequireAuth><LearnPage /></RequireAuth>} />
      <Route path="/vocab/child/:childId/quiz/:skill" element={<RequireAuth><QuizPage /></RequireAuth>} />
      <Route path="/vocab/child/:childId/plans"      element={<RequireAuth><ChildPlansPage /></RequireAuth>} />
      <Route path="/vocab/child/:childId/revision"   element={<RequireAuth><RevisionLanding /></RequireAuth>} />

      {/* Admin Portal */}
      <Route path="/admin" element={<RequireAuth admin><AdminLayout /></RequireAuth>}>
        <Route index element={<AdminDashboard />} />
        <Route path="words"    element={<AdminWords />} />
        <Route path="import"   element={<AdminImport />} />
        <Route path="review"   element={<AdminReview />} />
        <Route path="flags"    element={<AdminFlags />} />
        <Route path="missing"  element={<AdminMissing />} />
        <Route path="users"    element={<AdminUsers />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const block = (e) => e.preventDefault();
    document.addEventListener("contextmenu", block);
    return () => document.removeEventListener("contextmenu", block);
  }, []);

  return (
    <ThemeProvider>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </MotionConfig>
    </ThemeProvider>
  );
}

export default App;
